import React, { type FC, useMemo } from 'react';
import { type CreativeTypes } from '@bynder-studio/render-core';
import { ListItem, Thumbnail } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { Creative } from 'packages/store/creatives/types';
import { contentIconMap } from 'packages/pages/components/card/utils';
import { DesignsListRoot, DisabledText, IconWrapperElement, Spacing } from './DesignsList.styled';
import { ExportResult } from './types';
import { type RenditionConfiguration } from 'packages/services/renditionsService';
import { isDesignCompatibleWithProfiles } from './utils';

type DesignsListProps = {
    designs: Creative[];
    exportResults: ExportResult[];
    selectedProfiles: Record<CreativeTypes, RenditionConfiguration> | undefined;
};

const DesignsList: FC<DesignsListProps> = ({ designs, exportResults, selectedProfiles }) => {
    const { translate } = useTranslate();

    const exportResultMap = useMemo(() => {
        const map = new Map<number, ExportResult>();
        exportResults.forEach((result) => {
            map.set(result.designId, result);
        });

        return map;
    }, [exportResults]);

    return (
        <DesignsListRoot>
            <Spacing />
            {designs.map((design) => {
                const hasNoVariations = design.videosCount === 0;
                const isIncompatible = !isDesignCompatibleWithProfiles(design, selectedProfiles);
                const isDisabled = hasNoVariations || isIncompatible;

                return (
                    <ListItem
                        thumbnail={
                            <Thumbnail variant="content" imageUrl={design.thumbnailImage}>
                                {(hasNoVariations || exportResultMap.get(design.id)?.success === false) && (
                                    <Thumbnail.Indicator type="error" />
                                )}
                            </Thumbnail>
                        }
                        key={design.id}
                        subtext={translate('modal.bulk-export.design.subtext', {
                            pageCount: design.pageCounts,
                            variationCount: design.videosCount,
                        })}
                        rightElements={
                            <IconWrapperElement isDisabled={isDisabled}>
                                {contentIconMap[design.creativeType]}
                            </IconWrapperElement>
                        }
                        isDisabled={isDisabled}
                    >
                        {isDisabled ? <DisabledText>{design.name}</DisabledText> : design.name}
                    </ListItem>
                );
            })}
            <Spacing />
        </DesignsListRoot>
    );
};

export default DesignsList;
